import { CHARACTER_KINDS } from '@/gfx/procedural/characterKinds'
import {
  MAX_FIGHTERS,
  type CharacterKind,
  type Difficulty,
  type HazardKind,
  type PickupKind,
  type PropKind,
} from '@/components'
import { WIN_TARGETS, type MatchState } from '@/game/resources'
import { ACTIONS, type PlayerInput } from '@/input/InputManager'
import { ARENA_SIZES, ARENAS } from '@/game/arenas'

/**
 * Wire format between the browser and the game server, shared by both ends.
 *
 * Plain JSON over one WebSocket. The server owns the match; a client sends its
 * slot's input and draws the snapshots it receives. See docs/ONLINE.md and
 * docs/adr/0001-authoritative-server.md.
 */

/** Bumped on any change a client from an older cached build could misread. */
export const PROTOCOL_VERSION = 1

/** Snapshots per second, sent on the server's wall clock. */
export const SNAPSHOT_RATE = 30

/** «Каждый сам за себя» or «Люди против ботов». */
export type RoomMode = 'ffa' | 'coop'

/** What the host sets up; applied at the next start. */
export interface RoomConfig {
  arenaId: string
  arenaScale: number
  winsNeeded: number
  difficulty: Difficulty
  mode: RoomMode
  /**
   * Match size: bots fill the platform up to this many fighters. A minimum,
   * not a cap — players above it are never turned away.
   */
  size: number
}

export const DEFAULT_ROOM_CONFIG: RoomConfig = {
  arenaId: ARENAS[0].id,
  arenaScale: 1,
  winsNeeded: 3,
  difficulty: 1,
  mode: 'ffa',
  size: 4,
}

// ─────────────────────────────────────────────────────────────── messages ──

export type ClientMessage =
  | { t: 'hello'; v: number; nick: string; token?: string }
  | { t: 'create'; config: RoomConfig }
  | { t: 'join'; code: string }
  | { t: 'nick'; nick: string }
  | { t: 'leave' }
  | { t: 'pick'; model: CharacterKind }
  | { t: 'watch'; on: boolean }
  | { t: 'config'; config: RoomConfig }
  | { t: 'start' }
  | { t: 'abort' }
  | ({ t: 'in' } & InputMessage)
  | { t: 'ping'; c: number }

export type ErrorCode =
  /** The client is from another build; it should reload. */
  | 'version'
  | 'noroom'
  | 'full'
  /** A command the room refused, such as a start by someone who is not the host. */
  | 'refused'

export type ServerMessage =
  /** The session token; keep it to get your slot back after a reconnect. */
  | { t: 'welcome'; token: string }
  | ({ t: 'room' } & RoomView)
  | SnapshotMessage
  | { t: 'pong'; c: number; s: number }
  | { t: 'error'; code: ErrorCode; message?: string }

export interface MemberView {
  id: number
  nick: string
  model: CharacterKind
  watching: boolean
  connected: boolean
  /** Fighter slot in the running match, or null. */
  slot: number | null
}

export interface RoomView {
  code: string
  /** Member id of whoever receives this view. */
  you: number
  /** Member id of the host. */
  host: number
  config: RoomConfig
  members: MemberView[]
  playing: boolean
  /** Why a start would be refused right now, or null. */
  blocked: string | null
}

// ────────────────────────────────────────────────────────────────── input ──

/** One slot's input: the stick, and the five buttons as bit masks (`ACTIONS` order). */
export interface InputMessage {
  seq: number
  mx: number
  mz: number
  down: number
  pressed: number
}

export function packInput(input: PlayerInput, seq: number): InputMessage {
  let down = 0
  let pressed = 0
  ACTIONS.forEach((button, bit) => {
    if (input[button].down) down |= 1 << bit
    if (input[button].pressed) pressed |= 1 << bit
  })
  return { seq, mx: round(input.moveX, 3), mz: round(input.moveZ, 3), down, pressed }
}

/**
 * Writes a received input into a slot of the server's frame.
 *
 * Presses are OR-ed in, never overwritten: two messages arriving between two
 * ticks must both count, and the tick consumes them (`consumePresses`). The
 * stick is clamped to the unit disc, so no client can walk faster than a stick
 * pushed all the way.
 */
export function applyInput(message: InputMessage, into: PlayerInput): void {
  let x = finite(message.mx)
  let z = finite(message.mz)
  const mag = Math.hypot(x, z)
  if (mag > 1) {
    x /= mag
    z /= mag
  }
  into.moveX = x
  into.moveZ = z
  into.moveMag = Math.min(1, mag)
  ACTIONS.forEach((button, bit) => {
    into[button].down = (message.down & (1 << bit)) !== 0
    if (message.pressed & (1 << bit)) into[button].pressed = true
  })
}

// ─────────────────────────────────────────────────────────────── snapshot ──

/**
 * One snapshot of a running match: everything presentation reads.
 *
 * Every snapshot is complete. A client that joins, reconnects or drops one
 * needs nothing else, and the per-message deflate on the socket turns the
 * repeated static scenery into back-references.
 */
export interface SnapshotMessage {
  t: 'snap'
  /** Increments with every match in the room; a new value means start over. */
  matchId: number
  /** Fixed steps simulated so far in this match. */
  tick: number
  /** Server wall clock at the moment of the snapshot, in milliseconds. */
  time: number
  /** Highest input `seq` from this client the server has applied. */
  ack: number
  match: MatchView
  arena: ArenaView
  entities: EntityView[]
  /** Events since the previous snapshot, each stamped with its own time. */
  events: NetEvent[]
}

/** The match state with fighter handles as server entity ids. */
export type MatchView = Omit<MatchState, 'resumePhase'>

export interface ArenaView {
  scale: number
  collapsing: boolean
  warning: boolean
}

/**
 * One entity. `pose` is the rendered transform — already blended by the
 * server's own interpolation factor, so slow motion stays smooth even though
 * a frozen step lasts longer than a snapshot interval.
 */
export interface EntityView {
  /** The server's entity handle. */
  id: number
  pose: [x: number, y: number, z: number, angle: number]
  /** How to draw it; see `VisualSpawn`. */
  visual?: VisualSpawn
  /** Synced components by name; see `SYNCED` in net/replica.ts. */
  components: Record<string, Array<number | string>>
}

/** The scene-bridge call that created an entity's visual, replayed by clients. */
export type VisualSpawn =
  | ['character', player: number, color: number, kind: CharacterKind]
  | ['prop', kind: PropKind, seed: number, scale: number]
  | ['vortex', radius: number, color: number]
  | ['hazard', kind: HazardKind, radius: number]
  | ['shockwave', color: number]
  | ['bolt', color: number]
  | ['pickup', kind: PickupKind]

/**
 * A simulation event, by its name in `Events`.
 *
 * Stamped with the server's wall clock rather than a tick: hit stop makes the
 * ticks uneven in real time, and playback runs on the same clock as the
 * snapshots, so an event fires on the frame that shows it.
 */
export interface NetEvent {
  kind: string
  /** Server wall clock when it happened, in milliseconds. */
  at: number
  data: Record<string, unknown>
}

// ─────────────────────────────────────────────────────────────── helpers ──

export function round(value: number, digits: number): number {
  const f = 10 ** digits
  return Math.round(value * f) / f
}

function finite(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

/** Brings any config a client sent inside what the menu offers. */
export function sanitizeConfig(config: Partial<RoomConfig> | undefined): RoomConfig {
  const c = config ?? {}
  const fallback = DEFAULT_ROOM_CONFIG
  const fighters = Math.round(finite(c.size))
  return {
    arenaId: ARENAS.find((a) => a.id === c.arenaId)?.id ?? fallback.arenaId,
    arenaScale: ARENA_SIZES.find((s) => s.scale === c.arenaScale)?.scale ?? fallback.arenaScale,
    winsNeeded: WIN_TARGETS.find((w) => w === c.winsNeeded) ?? fallback.winsNeeded,
    difficulty: ([0, 1, 2] as const).find((d) => d === c.difficulty) ?? fallback.difficulty,
    mode: c.mode === 'coop' ? 'coop' : 'ffa',
    size: fighters >= 2 && fighters <= MAX_FIGHTERS ? fighters : fallback.size,
  }
}

export function isCharacterKind(value: unknown): value is CharacterKind {
  return CHARACTER_KINDS.includes(value as CharacterKind)
}
